import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

// Autocomplete indirizzo "da zero" (nessun SDK esterno): legge suggerimenti reali
// da OpenStreetMap Nominatim tramite il backend (/api/places/autocomplete) mentre
// l'utente digita, e passa lat/lng al genitore quando ne seleziona uno.
export default function AddressAutocomplete({ value, onChange, onSelect, className, dataTestId, placeholder, required }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const requestSeqRef = useRef(0);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const handleInputChange = (text) => {
        onChange(text);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (text.trim().length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            const seq = ++requestSeqRef.current;
            setLoading(true);
            try {
                const { data } = await api.get("/places/autocomplete", { params: { q: text.trim() } });
                if (seq !== requestSeqRef.current) return; // risposta obsoleta, superata da una digitazione più recente
                setSuggestions(data.suggestions || []);
                setOpen((data.suggestions || []).length > 0);
            } catch {
                if (seq === requestSeqRef.current) setSuggestions([]);
            } finally {
                if (seq === requestSeqRef.current) setLoading(false);
            }
        }, 400);
    };

    const pick = (s) => {
        onChange(s.address);
        onSelect(s);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="relative">
                <input
                    data-testid={dataTestId}
                    value={value}
                    required={required}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setOpen(true)}
                    onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={className}
                />
                {loading && <Loader2 size={14} className="animate-spin text-gold absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {open && suggestions.length > 0 && (
                <ul data-testid="address-suggestions-list" className="absolute z-30 left-0 right-0 mt-1 bg-ink-950 border border-white/10 shadow-xl max-h-64 overflow-y-auto">
                    {suggestions.map((s, i) => (
                        <li key={i}>
                            <button
                                type="button"
                                data-testid={`address-suggestion-${i}`}
                                onClick={() => pick(s)}
                                className="w-full text-left px-4 py-2.5 text-sm text-ink-100 hover:bg-gold/10 hover:text-gold flex items-start gap-2 transition-colors"
                            >
                                <MapPin size={14} className="text-gold mt-0.5 shrink-0" />
                                <span className="truncate">{s.display_name}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
